import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './TermsAcceptanceModal.css';

interface TermsAcceptanceModalProps {
    isOpen: boolean;
    onAccept: () => void;
    onDecline?: () => void;
    allowClose?: boolean;
    userName?: string;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({
    isOpen,
    onAccept,
    onDecline,
    allowClose = false,
    userName
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [hasScrolledToBottom, setHasScrolledToBottom] = React.useState(false);
    const [acceptChecked, setAcceptChecked] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        if (contentRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            // Consider "scrolled to bottom" when within 50px of the bottom
            if (scrollHeight - scrollTop - clientHeight < 50) {
                setHasScrolledToBottom(true);
            }
        }
    };

    const handleAccept = () => {
        if (acceptChecked) {
            onAccept();
        }
    };

    const handleDecline = () => {
        if (onDecline) {
            onDecline();
        } else {
            // Default behavior: go back to login
            navigate('/login');
        }
    };

    const handleViewFullTerms = () => {
        window.open('/terms-of-service', '_blank');
    };

    const handleViewPrivacyPolicy = () => {
        window.open('/privacy-policy', '_blank');
    };

    if (!isOpen) return null;

    return (
        <div className="terms-modal-overlay">
            <div className="terms-modal">
                <div className="terms-modal-header">
                    <h2>Terms of Service & Privacy Policy</h2>
                    {userName && <p className="welcome-text">Welcome, {userName}!</p>}
                    {allowClose && (
                        <button
                            className="terms-modal-close"
                            onClick={handleDecline}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div
                    className="terms-modal-content"
                    ref={contentRef}
                    onScroll={handleScroll}
                >
                    <div className="terms-section">
                        <h3>📋 Terms of Service</h3>
                        <p>
                            By using SeedDots, you agree to our terms of service. Key points include:
                        </p>
                        <ul>
                            <li>You must be at least 18 years old to use this platform</li>
                            <li>You are responsible for maintaining the security of your account</li>
                            <li>You agree to use the platform for lawful purposes only</li>
                            <li>We reserve the right to suspend accounts that violate our terms</li>
                            <li>You retain ownership of content you create on the platform</li>
                        </ul>
                        <button
                            className="terms-link-button"
                            onClick={handleViewFullTerms}
                        >
                            View Full Terms of Service →
                        </button>
                    </div>

                    <div className="terms-section">
                        <h3>🔒 Privacy Policy</h3>
                        <p>
                            We take your privacy seriously. Here's how we handle your information:
                        </p>

                        <div className="privacy-highlight">
                            <h4>⚠️ Important: Email Sharing</h4>
                            <p>
                                Your email address will be shared in the following scenarios:
                            </p>
                            <ul>
                                <li>
                                    <strong>Team Formation:</strong> When you join a team or accept team applications,
                                    your email is shared with team members to facilitate collaboration
                                </li>
                                <li>
                                    <strong>Pre-Release Projects:</strong> When you express interest in a pre-release project,
                                    your email is shared with the project owner so they can contact you about the launch
                                </li>
                                <li>
                                    <strong>Platform Messages:</strong> Your email may be used for website messages
                                    related to tasks, surveys, and team activities
                                </li>
                            </ul>
                        </div>

                        <div className="privacy-details">
                            <h4>Data We Collect</h4>
                            <ul>
                                <li>Account information (email, username, profile details)</li>
                                <li>Activity data (tasks completed, points earned, team memberships)</li>
                                <li>Usage analytics to improve the platform</li>
                            </ul>

                            <h4>Your Rights</h4>
                            <ul>
                                <li>Access and download your personal data</li>
                                <li>Request deletion of your account and data</li>
                                <li>Opt-out of non-essential communications</li>
                                <li>Update your information at any time</li>
                            </ul>
                        </div>

                        <button
                            className="terms-link-button"
                            onClick={handleViewPrivacyPolicy}
                        >
                            View Full Privacy Policy →
                        </button>
                    </div>

                    <div className="terms-section data-protection">
                        <h3>🛡️ Data Protection</h3>
                        <p>
                            We implement industry-standard security measures to protect your data.
                            Your information is stored securely and we never sell your personal data to third parties.
                        </p>
                    </div>
                </div>

                <div className="terms-modal-footer">
                    <div className="terms-acceptance-checkbox">
                        <input
                            type="checkbox"
                            id="accept-terms"
                            checked={acceptChecked}
                            onChange={(e) => setAcceptChecked(e.target.checked)}
                            disabled={!hasScrolledToBottom}
                        />
                        <label htmlFor="accept-terms">
                            I have read and agree to the Terms of Service and Privacy Policy
                            {!hasScrolledToBottom && (
                                <span className="scroll-hint"> (Please scroll to the bottom first)</span>
                            )}
                        </label>
                    </div>

                    <div className="terms-modal-actions">
                        <button
                            className="btn btn-secondary"
                            onClick={handleDecline}
                        >
                            Decline
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleAccept}
                            disabled={!acceptChecked}
                        >
                            Accept & Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsAcceptanceModal;
